import React, { FunctionComponent, useMemo, useState } from 'react'
import FormBuilder from '@afterpaytouch/form'
import { useTranslation } from 'next-i18next'

import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import {
  useCountry,
  useInternationalShippingEnabled,
  useCheckoutData,
  useConsumerPreferredLocale,
  useInternationalShippingCountry,
  useIsIntlShippingSupportConsumerCountry,
  useCrossBorderTrade,
} from '../../state'
import { getSchemaAndFormData, ShippingAddressFormSchemaData } from './utils'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { ShippingAddress, ShippingAddressFormData } from '../../model'
import { AllCountryCode, Button, Text, useIsDesignLanguage, DesignLanguage, usePhoneNumber, useToast } from '@afterpaytouch/core'
import { ErrorCode, isPortalApiError, PortalApiError } from '@afterpaytouch/portal-api'
import { TrackingEvent } from '../../model/monitoring'
import { Country } from '@afterpaytouch/portal-api/types'
import { useNonInitialEffect } from '../../utils/hooks'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'

interface ShippingAddressFormProps {
  editMode?: boolean
  id?: number
  initialFormValues: ShippingAddressFormData
  addressToEdit?: ShippingAddress
  onSaveHandler: (formData: ShippingAddressFormData) => void
  saveButtonLabel: string
}

export const ShippingAddressForm: FunctionComponent<ShippingAddressFormProps> = ({
  editMode = false,
  addressToEdit,
  initialFormValues,
  onSaveHandler,
  saveButtonLabel,
}) => {
  const { t } = useTranslation()
  const { buttonSize, extraSmallTextSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const consumerCountry = useCountry()
  const isInternationalShippingEnabled = useInternationalShippingEnabled()
  const checkout = useCheckoutData()
  const preferredLocale = useConsumerPreferredLocale()
  const setToast = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [errors, setErrors] = useState<ErrorCode>()
  const checkoutShippingCountry = useInternationalShippingCountry()
  const isConsumerCountrySupported = useIsIntlShippingSupportConsumerCountry()
  const isCrossBorderEnabled = useCrossBorderTrade()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const getShippingCountry = (): string | undefined => {
    if (isInternationalShippingEnabled) {
      if (!isUndefinedOrEmpty(checkoutShippingCountry)) {
        return checkoutShippingCountry
      }
      if (!isConsumerCountrySupported) {
        return undefined
      }
    }
    return consumerCountry
  }

  const defaultInternationalShippingValue = getShippingCountry()

  const [shippingCountry, setShippingCountry] = useState(defaultInternationalShippingValue)

  const { parseMobile } = usePhoneNumber({ country: addressToEdit?.countryCode })

  const testIdType = editMode ? 'edit' : 'new'

  const { schema, initialFormData } = useMemo(() => {
    const intlShipping = {
      isEnabled: isInternationalShippingEnabled,
      supportedShippingCountries: checkout?.shippingOptions?.supportedShippingCountries ?? [],
      shippingCountry: checkout?.shippingOptions?.shippingCountry,
    }

    const components = {
      footer: () => {
        return (
          <Button type='submit' testNameSpace={`${testIdType}-address-submit`} loading={isLoading} disabled={!isFormReady || isLoading} padding='Fluid'>
            {saveButtonLabel}
          </Button>
        )
      },
      ...(isInternationalShippingEnabled && { intlShipping }),
      preferredLocale,
      handleManualModeChange: (isManualMode) => handleManualModeChange(isManualMode),
      defaultShippingValue: defaultInternationalShippingValue ?? '',
    }

    const phoneNumber = parseMobile(initialFormValues?.mobile) // then replace mobile here with this value

    return getSchemaAndFormData(
      components,
      t,
      shippingCountry as AllCountryCode,
      consumerCountry,
      initialFormValues,
      editMode,
      addressToEdit,
      phoneNumber,
      errors,
      isInternationalShippingEnabled,
      isArcadeDesignLanguage
    )
  }, [t, buttonSize, extraSmallTextSize, shippingCountry, isLoading, isFormReady, errors, isInternationalShippingEnabled, initialFormValues, consumerCountry])

  const [formData, setFormData] = useState(initialFormData)

  const handleSaveAddressError = (e: Error): void => {
    logEvent(TrackingEvent.ERROR_ON_SAVE_SHIPPING_ADDRESS)
    if (isPortalApiError(e)) {
      const portalError = e as PortalApiError
      switch (portalError.errorCode) {
        case ErrorCode.InvalidPhoneNumber:
          setErrors(ErrorCode.InvalidPhoneNumber)
          break
        default: {
          if (isArcadeDesignLanguage) {
            setToast({
              message: () => (
                <>
                  <Text bold>{t('addNewAddress:error.serverErrorMessage.heading')}</Text>
                  <Text>{t('addNewAddress:error.serverErrorMessage.message')}</Text>
                </>
              ),
              kind: 'Failure',
            })
          } else {
            setToast({
              message: t('addNewAddress:error.serverError'),
              kind: 'Failure',
            })
          }
          break
        }
      }
    }
  }

  const handleManualModeChange = (isManualMode: boolean): void => {
    if (isManualMode) {
      logEvent(TrackingEvent.PRESSED_ENTER_ADDRESS_MANUALLY)
    } else {
      logEvent(TrackingEvent.PRESSED_SEARCH_FOR_ADDRESS)
    }
  }

  const handleOnChange = (data: ShippingAddressFormSchemaData): void => {
    if (formData.isPreferredAddress !== data.isPreferredAddress) {
      logEvent(TrackingEvent.TOGGLED_PREFERRED_ADDRESS, {
        toggledValue: data.isPreferredAddress,
      })
    }
    if (formData.mobile !== data.mobile) {
      if (errors === ErrorCode.InvalidPhoneNumber) {
        setErrors(undefined)
      }
    }
    if (isInternationalShippingEnabled) {
      if (data.shippingCountry !== shippingCountry && !isUndefinedOrEmpty(data.shippingCountry)) {
        setShippingCountry(data.shippingCountry)
        logEvent(TrackingEvent.CHANGED_SHIPPING_COUNTRY, { country: data.shippingCountry })
      }
    }
    setFormData(data)
  }

  const handleStateChange = (isValid: boolean): void => {
    setIsFormReady(isValid)
  }

  const handleSubmit = async (data: ShippingAddressFormSchemaData): Promise<void> => {
    setIsLoading(true)

    const requestFormData = {
      ...data,
      ...data.address,
      countryCode: (data?.address?.countryCode ?? consumerCountry) as Country,
    }
    try {
      await onSaveHandler(requestFormData)
    } catch (e) {
      handleSaveAddressError(e)
      setIsLoading(false)
    } finally {
      logEvent(TrackingEvent.PRESSED_SAVE_NEW_SHIPPING_ADDRESS, {
        initialFormAddressCountryCode: initialFormValues.countryCode,
        ...(editMode && {
          addressToEditCountryCode: addressToEdit?.countryCode,
          addressToEditPostcode: addressToEdit?.postcode,
          addressToEditState: addressToEdit?.state,
        }),
        isEditMode: editMode,
        consumerCountryCode: consumerCountry,
        isInternationalShipping: isInternationalShippingEnabled,
        isCrossBorder: isCrossBorderEnabled?.isCrossBorder,
      })
    }
  }

  useNonInitialEffect(() => {
    setShippingCountry(defaultInternationalShippingValue)
  }, [isInternationalShippingEnabled])

  return (
    <>
      <div className={styles.newAddressFormWrapper}>
        <MaskedElement actionName='Shipping Address Form'>
          <FormBuilder
            schema={schema}
            testNameSpace={`${testIdType}-address`}
            onSubmit={handleSubmit}
            onChange={handleOnChange}
            onStateChange={handleStateChange}
            disabled={isLoading}
          />
          <TrackOnMount
            eventName={`Viewed ${editMode ? 'Edit' : 'New'} Shipping Address Form Check`}
            eventProperties={{
              pageName: `${testIdType} Address`,
              initialFormAddressCountryCode: initialFormValues.countryCode,
              ...(editMode && {
                addressToEditCountryCode: addressToEdit?.countryCode,
                addressToEditPostcode: addressToEdit?.postcode,
                addressToEditState: addressToEdit?.state,
              }),
              consumerCountryCode: consumerCountry,
              isInternationalShipping: isInternationalShippingEnabled,
              isCrossBorder: isCrossBorderEnabled?.isCrossBorder,
            }}
          />
        </MaskedElement>
      </div>
    </>
  )
}
